import { StandardEvents } from '@shopify/events';

/**
 * Sends the GA4 shopping events the Google & YouTube app doesn't send:
 * - `view_item_list` when a collection page shows its products,
 * - `view_cart` when the cart page loads or the cart drawer opens,
 * - `remove_from_cart` when a cart line is removed or its quantity lowered.
 *
 * They go through the app's own Google tag on the page (`window.gtag`), so they reach the same
 * GA4 property, visitor and session as the app's events. Items use the app's item shape
 * (Merchant Center IDs), so GA4 reports join them with the app's views, adds and purchases.
 *
 * An event the app sends itself (it tags its events with `shopify_event_name`) is skipped, so
 * these retire on their own if the app starts sending them. Nothing is sent when the Google tag
 * never loads (ad blockers, DNS filters), just as for the app's events.
 *
 * Listeners register when this module runs, before the modules that define the view-event
 * elements (`snippets/scripts.liquid` loads it first), so no view event is missed.
 */

/** The country in the app's Merchant Center product IDs: `shopify_US_<product id>_<variant id>`. */
const MERCHANT_CENTER_COUNTRY = 'US';
/** How long the app gets to send an event itself before this module sends it. */
const APP_GRACE_MS = 1500;
/** How long to wait for the app's Google tag to load. */
const TAG_TIMEOUT_MS = 15000;

/**
 * The cart's lines as last read (on cart views and after adds), kept up to date by later cart
 * changes, so a removal knows what the line held before. `null` until the cart is read.
 * @type {CartItem[] | null}
 */
let cartItems = null;

/**
 * A line or product in the field names of Shopify's cart JSON (`/cart.js`).
 * `snippets/ga4-item-list.liquid` renders collection products in the same shape.
 * @typedef {object} CartItem
 * @property {string} [key]
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} product_title
 * @property {string} vendor
 * @property {string} product_type
 * @property {number} final_price - Price per unit in cents.
 * @property {number} [quantity]
 * @property {string | null} variant_title
 * @property {string | null} sku
 */

document.addEventListener(StandardEvents.collectionView, (event) => {
  const json = event.target instanceof Element ? event.target.querySelector('script[data-ga4-item-list]')?.textContent : null;
  if (!json) {
    return;
  }

  /** @type {{ id: string, name: string, items: CartItem[] }} */
  const list = JSON.parse(json);
  if (list.items.length === 0) {
    return;
  }

  sendEvent('view_item_list', {
    currency: Shopify.currency.active,
    item_list_id: list.id,
    item_list_name: list.name,
    items: list.items.map((item, index) => ({ ...toGaItem(item, 1), index })),
  });
});

document.addEventListener(StandardEvents.cartView, async () => {
  const sinceIndex = dataLayerLength();
  const cart = await fetchCart();
  if (!cart) {
    return;
  }

  cartItems = cart.items;
  if (cart.item_count === 0) {
    return;
  }

  sendEvent(
    'view_cart',
    {
      currency: cart.currency,
      value: cart.total_price / 100,
      items: cart.items.map((item) => toGaItem(item)),
    },
    sinceIndex
  );
});

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  const sinceIndex = dataLayerLength();
  const before = cartItems;
  let result;
  try {
    result = await event.promise;
  } catch {
    return;
  }

  if (event.action === 'add') {
    // The added line isn't in the cached lines; reread the cart so a later removal finds it.
    cartItems = (await fetchCart())?.items ?? null;
    return;
  }

  if (!result?.cart || (result.userErrors?.length ?? 0) > 0) {
    cartItems = null;
    return;
  }

  cartItems = updateQuantities(before, result.cart.lines);
  if (!before) {
    console.log('[ga4-events] cart change not checked for removals: the cart hadn\'t been read yet');
    return;
  }

  for (const line of event.lines) {
    const item = before.find((candidate) => candidate.key === lineKey(line.id));
    const removed = item ? (item.quantity ?? 0) - line.quantity : 0;
    if (!item || removed <= 0) {
      continue;
    }

    sendEvent(
      'remove_from_cart',
      {
        currency: result.cart.cost.totalAmount.currencyCode,
        value: (item.final_price * removed) / 100,
        items: [toGaItem(item, removed)],
      },
      sinceIndex
    );
  }
});

/**
 * A GA4 item in the shape the Google & YouTube app sends.
 * @param {CartItem} item
 * @param {number} [quantity]
 */
function toGaItem(item, quantity = item.quantity) {
  return {
    id: `shopify_${MERCHANT_CENTER_COUNTRY}_${item.product_id}_${item.variant_id}`,
    name: item.product_title,
    brand: item.vendor,
    category: item.product_type,
    price: item.final_price / 100,
    quantity,
    variant: item.variant_title,
    variant_id: String(item.variant_id),
    sku: item.sku,
  };
}

/**
 * Sends a GA4 event through the app's Google tag once it has loaded, unless the app sent the
 * same event itself since the shopper's action.
 * @param {string} eventName
 * @param {Record<string, unknown>} params
 * @param {number} [sinceIndex] - The dataLayer length when the shopper acted.
 */
async function sendEvent(eventName, params, sinceIndex = dataLayerLength()) {
  const measurementId = await waitForGoogleTag();
  if (!measurementId || !window.gtag) {
    console.log(`[ga4-events] ${eventName} not sent: the Google tag didn't load`);
    return;
  }

  await sleep(APP_GRACE_MS);
  if (appSent(eventName, sinceIndex)) {
    console.log(`[ga4-events] ${eventName} not sent: the Google & YouTube app sent it`);
    return;
  }

  window.gtag('event', eventName, { ...params, send_to: measurementId });
  console.log(`[ga4-events] ${eventName} sent to ${measurementId}`, params);
}

/**
 * Resolves with the GA4 measurement ID the app configured once its tag has loaded, or `null`.
 * @returns {Promise<string | null>}
 */
async function waitForGoogleTag() {
  const deadline = Date.now() + TAG_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const config = Array.from(window.dataLayer ?? []).find((entry) => entry[0] === 'config' && String(entry[1]).startsWith('G-'));
    const measurementId = config ? String(config[1]) : null;
    if (measurementId && window.google_tag_manager?.[measurementId]) {
      return measurementId;
    }
    await sleep(250);
  }
  return null;
}

function dataLayerLength() {
  return window.dataLayer?.length ?? 0;
}

/**
 * Whether the app pushed this event to the dataLayer after `sinceIndex`.
 * @param {string} eventName
 * @param {number} sinceIndex
 */
function appSent(eventName, sinceIndex) {
  return Array.from(window.dataLayer ?? [])
    .slice(sinceIndex)
    .some((entry) => entry[0] === 'event' && entry[1] === eventName && entry[2]?.shopify_event_name);
}

/** @returns {Promise<{ currency: string, item_count: number, total_price: number, items: CartItem[] } | null>} */
async function fetchCart() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    return response.ok ? await response.json() : null;
  } catch (error) {
    console.log('[ga4-events] cart not read:', error);
    return null;
  }
}

/**
 * The cached lines with the quantities after an update; lines the update removed drop out.
 * @param {CartItem[] | null} items
 * @param {{ id: string, quantity: number }[]} lines
 */
function updateQuantities(items, lines) {
  if (!items) {
    return null;
  }

  const quantities = new Map(lines.map((line) => [lineKey(line.id), line.quantity]));
  return items.filter((item) => quantities.has(item.key ?? '')).map((item) => ({ ...item, quantity: quantities.get(item.key ?? '') }));
}

/**
 * The cart line key (`<variant id>:<hash>`) from a line ID, which may also be a CartLine GID.
 * @param {string} id
 */
function lineKey(id) {
  return String(id).replace('gid://shopify/CartLine/', '').split('?')[0];
}

/** @param {number} ms */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
